import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import postDrugSearch, {
  PostDrugRequest,
  PostDrugResponse
} from 'src/api/requests/postDrugSearch';
import {
  AllAnalogues,
  DrugItem,
  TopDrug,
  UserLocation,
  TradenameItem,
  DrugReview,
  OtherTradeName
} from 'src/api/types';
import { useRouter } from 'next/router';
import useLocationStore from 'src/hooks/useLocationStore';
import Head from 'next/head';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  generateTradenamePageLink,
  SearchPageQueryType
} from 'src/utils/links';
import usePostSearchFilters, {
  getSearchRequestObject
} from 'src/api/hooks/usePostSearchFilters';
import {
  getProductByLetterPageAndCityMetaData,
  getProductByLetterPageMetaData
} from 'src/utils/seo';
import useNotification, { NotificationType } from 'src/hooks/useNotification';
import getTopDrugsByTranslitName from 'src/api/requests/getTopDrugsByTranslitName';
import isEmpty from 'lodash/isEmpty';
import { BreadCrumbs } from 'src/shared-components/design-system/BreadCrumbs';
import Constraints from 'src/shared-components/Constraints/Constraints';
import Page from 'src/shared-components/Page/Page';
import DrugsCarouselContainer from 'src/shared-components/TopDrugs/DrugsCarouselContainer';
import { ItemListName } from 'src/api/analytic.types';
import { getAnalyticPreparedDrugList } from 'src/hooks/useAnalytics/useAnalytics.helpers';
import useGTAG, { GTAGPrefix } from 'src/hooks/useGTAG';
import ReviewsComponent from 'src/shared-components/Reviews/ReviewsComponent';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';

import getAnaloguesByTranslitName from '../../api/requests/getAnaloguesByTranslitName';
import DrugTable from '../category/components/DrugTable';
import ShowMoreSeoText from '../category/components/ShowMoreSeoText';
import AnalogueTabContent from '../drug/components/TabContent/AnalogueTabContent';
import SearchResultsHeader from '../search/components/SearchResultsHeader';
import SearchResultsSection from '../search/components/SearchResultsSection';
import TextBlock from './TextBlock';
import ListOfCities from './components/ListOfCities';
import trns from './trns';
import { getTranslitNameCityAndPage } from '../../utils/urlParamsHelper';
import DoctorCardContainer from '../home/components/DoctorCardContainer/DoctorCardContainer';
import AnaloguesList from '../../shared-components/AnaloguesList/AnaloguesList';

interface Props {
  searchDrugs: PostDrugResponse;
  analoguesList: AllAnalogues;
  otherTradeNamesList: OtherTradeName[];
  ssrTopDrugs: TopDrug[];
  tradenameData: TradenameItem;
  selectedCity?: UserLocation | null;
  citiesList?: UserLocation[];
  reviewsList: DrugReview[];
  ssrSearchObject: PostDrugRequest;
}

const TradenameContainer: FC<Props> = ({
  searchDrugs,
  analoguesList: ssrAnaloguesList,
  ssrTopDrugs,
  tradenameData,
  selectedCity = null,
  citiesList = [],
  reviewsList,
  otherTradeNamesList,
  ssrSearchObject
}) => {
  const router = useRouter();
  const { query, locale } = router;
  const {
    userLocation,
    storeInitilized,
    userLocationUpdatedManually,
    setUserLocation
  } = useLocationStore();
  const intl = useIntl();
  const { trackGtagEvent } = useGTAG();
  const { showNotification } = useNotification();
  const search = tradenameData?.name || null;
  const { letter, minPrice, maxPrice } = tradenameData;
  const { filters, sortBy, sortDirection, drug, params } = query;
  const { translitNameOfCity, pageNumber } = getTranslitNameCityAndPage(params);

  const itemListName =
    selectedCity === null
      ? ItemListName.Tradename
      : ItemListName.TradenameWithTheCity;

  const [drugList, setDrugList] = useState<DrugItem[]>(searchDrugs?.drugs);
  const [analoguesList, setAnaloguesList] =
    useState<AllAnalogues>(ssrAnaloguesList);
  const [totalDrugsCount, setTotalDrugsCount] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedPages, setSelectedPages] = useState<number[]>([pageNumber]);
  const [topDrugsList, setTopDrugsList] = useState<TopDrug[]>(null);
  const [searchCount, setSearchCount] = useState<number>(0);

  const textSearch = (search as string) || null;

  const analogsRef = useRef(null);

  const path = () => {
    if (Number(textSearch[0])) return '/letter/0-9';
    if (textSearch.charCodeAt(0) > 64 && textSearch.charCodeAt(0) < 122)
      return '/letter/a-z';
    return `/letter/${letter?.translitName}`;
  };

  useEffect(() => {
    if (pageNumber) {
      setSelectedPages([pageNumber]);
    }
  }, [pageNumber]);

  useEffect(() => {
    if (ssrTopDrugs?.length) {
      setTopDrugsList(ssrTopDrugs);
    }
  }, [
    // TODO: replace array with some primitive value.
    ssrTopDrugs
  ]);

  useEffect(() => {
    if (selectedCity?.name && userLocation?.name !== selectedCity?.name) {
      setUserLocation(selectedCity);
    }
  }, [selectedCity?.name, setUserLocation]);

  const getSearchObject = useCallback(
    (loadMorePageNumber?: number) => {
      const searchObject = getSearchRequestObject(
        loadMorePageNumber || pageNumber,
        null,
        filters,
        sortBy,
        sortDirection
      );
      return {
        ...searchObject,
        filters: [
          ...searchObject.filters,
          {
            id: 'tradeName',
            valueIds: [search]
          }
        ]
      };
    },
    [pageNumber, search, filters, sortBy, sortDirection]
  );

  const searchObject = useMemo(
    () => getSearchObject(),
    [getSearchObject, pageNumber, search, filters, sortBy, sortDirection]
  );

  const { data: filtersData, isLoading: isFiltersLoading } =
    usePostSearchFilters(searchObject);

  const debouncedDrugsUpdate = useCallback(
    debounce(() => {
      const newSearchObject = getSearchObject();
      if (isEqual(newSearchObject, ssrSearchObject)) {
        // new request object is the same as was for initial page render
        // set SSR values to state instead of API calls
        const drugs = getAnalyticPreparedDrugList({
          drugs: searchDrugs?.drugs || [],
          itemListName
        });
        setDrugList(drugs);
        setTotalDrugsCount(searchDrugs?.totalCount || 1);
        setIsLoading(false);
      } else {
        setIsLoading(true);
        const drugSearchRequest = postDrugSearch(searchObject);
        drugSearchRequest
          .then((drugSearchResult) => {
            const drugs = getAnalyticPreparedDrugList({
              drugs: drugSearchResult?.drugs || [],
              itemListName
            });
            setDrugList(drugs);
            setTotalDrugsCount(drugSearchResult?.totalCount || 1);
          })
          .catch(() => {
            // Clean up drug list because error happend
            setDrugList([]);
            setTotalDrugsCount(1);
          })
          .finally(() => {
            setIsLoading(false);
            setSearchCount((count) => count + 1);
          });
      }
    }, 250),
    [
      search,
      pageNumber,
      userLocation?.name,
      locale,
      filters,
      sortBy,
      sortDirection
    ]
  );

  useEffect(() => {
    if (storeInitilized) {
      debouncedDrugsUpdate();
    }
  }, [storeInitilized, debouncedDrugsUpdate]);

  useEffect(() => {
    if (userLocation?.name && userLocationUpdatedManually) {
      getAnaloguesByTranslitName({
        translitname: tradenameData?.translitName
      }).then((analogs) => {
        setAnaloguesList(analogs);
      });

      getTopDrugsByTranslitName({
        translitname: tradenameData?.translitName
      }).then((list) => {
        setTopDrugsList(list);
      });
      if (translitNameOfCity) {
        // Remove `translitNameOfCity` from URL if city was updated manually on tradename page
        router.push(
          generateTradenamePageLink({
            page: pageNumber,
            drug: query?.drug as string
          }),
          undefined,
          { scroll: false }
        );
      }
    }
  }, [userLocation?.name, userLocationUpdatedManually]);

  useEffect(() => {
    if (textSearch) {
      trackGtagEvent(
        `${GTAGPrefix.tradename}_open`,
        textSearch,
        'tradename_name'
      );
    }
  }, [textSearch]);

  const handleLoadMoreSubmit = (loadMorePageNumber: number) => {
    trackGtagEvent('tradename_load_more');
    setIsLoading(true);
    const drugSearchRequest = postDrugSearch(
      getSearchObject(loadMorePageNumber)
    );
    drugSearchRequest
      .then((drugSearchResult) => {
        // Add newly loaded drugs to the existing list
        const extendedDrugs = [...drugList, ...(drugSearchResult?.drugs || [])];
        setDrugList(extendedDrugs);
        setSelectedPages([...selectedPages, loadMorePageNumber]);
      })
      .catch(() => {
        showNotification({
          message: intl.formatMessage(trns.unExpectedError),
          type: NotificationType.Error
        });
      })
      .finally(() => {
        setIsLoading(false);
        setSearchCount((count) => count + 1);
      });
  };

  const getSeoData = translitNameOfCity
    ? getProductByLetterPageAndCityMetaData
    : getProductByLetterPageMetaData;

  const seoData = getSeoData({
    drugName: search,
    locale,
    minPrice,
    cityName: selectedCity?.name
  });

  const pageLinkGenerationMethod = ({
    params: additionalParams,
    ...restQuery
  }: SearchPageQueryType) => {
    return generateTradenamePageLink(
      {
        page: pageNumber,
        ...restQuery
      },
      translitNameOfCity
    );
  };

  const topDrugs = topDrugsList || ssrTopDrugs;
  const isSEOTextHidden = pageNumber > 1;

  const drugListWithAnalyticData = useMemo(
    () =>
      getAnalyticPreparedDrugList({
        drugs: drugList,
        itemListName
      }),
    [drugList, searchCount]
  );

  return (
    <>
      <Head>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
      </Head>
      <Page>
        <BreadCrumbs
          items={[
            {
              id: 'home',
              title: <FormattedMessage {...trns.homeBreadCrumb} />,
              href: '/'
            },
            {
              id: 'letter',
              title: (
                <FormattedMessage
                  {...trns.letterName}
                  values={{ letter: letter?.name.toUpperCase() }}
                />
              ),
              href: `${path()}`
            },
            {
              id: 'product',
              title: textSearch,
              href: `/tradename/${drug}`
            }
          ]}
        />
        <SearchResultsHeader
          textSearch={(search as string) || null}
          drugList={drugListWithAnalyticData}
          isLoading={isLoading}
          isTradeNamePage={true}
          selectedCity={selectedCity}
          translitNameOfCity={translitNameOfCity}
        />
        {isEmpty(drugListWithAnalyticData) && !isLoading ? (
          <DrugsCarouselContainer gtagPrefix={GTAGPrefix.top} />
        ) : (
          <Constraints className="flex w-full pt-8 pb-7 relative w-[90%]">
            <SearchResultsSection
              filters={filtersData?.filters || []}
              drugList={drugListWithAnalyticData}
              gtagPrefix={GTAGPrefix.tradename}
              totalDrugsCount={totalDrugsCount}
              selectedPages={selectedPages}
              isLoading={isLoading}
              handleLoadMoreSubmit={handleLoadMoreSubmit}
              getPaginationLink={(page: number) =>
                pageLinkGenerationMethod({
                  ...router.query,
                  page
                })
              }
              pageLinkGenerationMethod={pageLinkGenerationMethod}
              textSearch={textSearch}
              isFiltersLoading={isFiltersLoading}
              isAnalogBannerVisible={false}
              analogsRef={analogsRef}
              isHidePaginationAllow={true}
              isTradenameBannerVisible
            />
          </Constraints>
        )}
        <>
          <Constraints className="pt-8 pb-7 relative sm:w-[90%]">
            <div ref={analogsRef} className="scroll-mt-20">
              <AnalogueTabContent
                analoguesList={analoguesList}
                tradeName={textSearch}
              />
            </div>
          </Constraints>
          <Constraints className="pt-16 pb-7 relative sm:w-[90%]">
            <div className="lm:mx-0 mx-4">
              <ReviewsComponent
                drugTranslitname={tradenameData?.translitName}
                averageRate={tradenameData?.averageRate}
                rateCount={tradenameData?.rateCount}
                reviews={reviewsList}
                tradenameData={tradenameData}
                isTradenamePage
              />
            </div>
          </Constraints>
          <Constraints className="sm:w-[90%] compendium-constraints w-full">
            <AnaloguesList otherTradeNamesList={otherTradeNamesList} />
          </Constraints>
          {!isSEOTextHidden && (
            <TextBlock
              tradename={textSearch}
              minPrice={minPrice}
              maxPrice={maxPrice}
            />
          )}
          {!isSEOTextHidden &&
            tradenameData?.description &&
            !translitNameOfCity && (
              <Constraints className="pt-8 pb-7 relative w-[90%]">
                <ShowMoreSeoText
                  title={tradenameData?.name}
                  text={tradenameData?.description}
                />
              </Constraints>
            )}
          {tradenameData?.doctorReviewer && pageNumber < 2 ? (
            <Constraints className="pb-7 relative w-[90%]">
              <DoctorCardContainer
                doctorReviewer={tradenameData.doctorReviewer}
              />
            </Constraints>
          ) : null}
          {!!topDrugs?.length && !isSEOTextHidden && (
            <Constraints className="pt-8 pb-7 relative w-[90%]">
              <DrugTable drugList={topDrugs} textSearch={textSearch} />
            </Constraints>
          )}
        </>
        {citiesList && tradenameData?.tradeNameInCity && (
          <ListOfCities
            tradeName={textSearch || null}
            citiesList={citiesList}
            cityClickCallback={(location) => {
              setUserLocation(location);
              getAnaloguesByTranslitName(
                {
                  translitname: tradenameData?.translitName
                },
                { locationOverride: location }
              ).then((analogs) => {
                setAnaloguesList(analogs);
              });

              getTopDrugsByTranslitName(
                {
                  translitname: tradenameData?.translitName
                },
                { locationOverride: location }
              ).then((list) => {
                setTopDrugsList(list);
              });
            }}
          />
        )}
      </Page>
    </>
  );
};

export default TradenameContainer;
